// src/components/CompetitionJudges.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    IconButton,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert
} from '@mui/material';
import { Add, Delete, Person } from '@mui/icons-material';
import { getCompetitionJudges, removeCompetitionJudge } from '../api/competitionApi';
import AddJudgeDialog from './AddJudgeDialog';

export default function CompetitionJudges({ competitionId, isAdminView, onUpdate }) {
    const [judges, setJudges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [judgeToDelete, setJudgeToDelete] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        loadJudges();
    }, [competitionId]);

    const loadJudges = async () => {
        try {
            setLoading(true);
            const data = await getCompetitionJudges(competitionId);
            setJudges(data);
        } catch (error) {
            console.error('Error loading judges:', error);
            setErrorMessage('Failed to load judges');
        } finally {
            setLoading(false);
        }
    };

    const handleAddJudge = () => {
        setOpenAddDialog(true);
    };

    const handleCloseAddDialog = () => {
        setOpenAddDialog(false);
        loadJudges();
        if (onUpdate) {
            onUpdate();
        }
    };

    const handleDeleteClick = (judge) => {
        setJudgeToDelete(judge);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setJudgeToDelete(null);
    };

    const handleDeleteJudge = async () => {
        try {
            setErrorMessage('');
            await removeCompetitionJudge(competitionId, judgeToDelete.id);
            setDeleteConfirmOpen(false);
            setJudgeToDelete(null);
            loadJudges();
            if (onUpdate) {
                onUpdate();
            }
        } catch (error) {
            console.error('Error deleting judge:', error);
            setErrorMessage(error.response?.data?.error || 'Failed to delete judge');
            setDeleteConfirmOpen(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                    {errorMessage}
                </Alert>
            )}

            {isAdminView && (
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: { xs: 2, sm: 0 },
                    mb: 3 
                }}>
                    <Typography variant="h6" fontWeight="bold">
                        Judges
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleAddJudge}
                        fullWidth={{ xs: true, sm: false }}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                        Add Judge
                    </Button>
                </Box>
            )}

            {judges.length === 0 ? (
                <Paper sx={{ p: 3 }}>
                    <Typography align="center" color="textSecondary">
                        {isAdminView 
                            ? 'No judges registered yet. Add your first judge!'
                            : 'No judges registered for this competition.'}
                    </Typography>
                </Paper>
            ) : (
                <List>
                    {judges.map((judge) => (
                        <Paper key={judge.id} sx={{ mb: 1 }}>
                            <ListItem
                                secondaryAction={
                                    isAdminView && (
                                        <IconButton
                                            edge="end"
                                            aria-label="delete"
                                            onClick={() => handleDeleteClick(judge)}
                                            sx={{ 
                                                backgroundColor: '#d32f2f',
                                                color: '#ffffff !important',
                                                '& svg': {
                                                    color: '#ffffff !important'
                                                },
                                                '&:hover': {
                                                    backgroundColor: '#b71c1c',
                                                    color: '#ffffff !important',
                                                    '& svg': {
                                                        color: '#ffffff !important'
                                                    }
                                                },
                                                '&:focus': {
                                                    backgroundColor: '#d32f2f',
                                                    color: '#ffffff !important',
                                                    '& svg': {
                                                        color: '#ffffff !important'
                                                    }
                                                }
                                            }}
                                        >
                                            <Delete sx={{ color: '#ffffff !important' }} />
                                        </IconButton>
                                    )
                                }
                            >
                                <Person sx={{ mr: 2, color: 'primary.main' }} />
                                <ListItemText
                                    primary={judge.user.fullName || 'Unknown'}
                                    secondary={judge.user.email}
                                />
                            </ListItem>
                        </Paper>
                    ))}
                </List>
            )}

            {/* Add Judge Dialog */}
            <AddJudgeDialog
                open={openAddDialog}
                onClose={handleCloseAddDialog}
                competitionId={competitionId}
                existingJudges={judges}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: { xs: '90vw', sm: '500px' }
                    }
                }}
            >
                <DialogTitle
                    id="delete-dialog-title"
                    sx={{
                        fontWeight: 'medium',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                >
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to remove {judgeToDelete?.user?.fullName || 'this judge'} as a judge? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                    <Button
                        onClick={handleDeleteCancel}
                        color="primary"
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteJudge}
                        color="error"
                        variant="contained"
                        autoFocus
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

